import { useEffect, useState } from "react";
import { X } from "lucide-react";

const ProductCodes = ({ searchResults, setSelectedProductCode, setShowProductCodes, setShowPredicateBubbleMap }) => {

  const [productCodes, setProductCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removedCodes, setRemovedCodes] = useState(new Set());

  const defaultTechnicalCharacteristics = [
    "Intended Use / Indications for use",
    "Contraindications",
    "Operating Principle",
    "Mechanism of action",
    "Performance",
    "Shelf Life",
    "Packaging configuration and Materials",
    "Sterilization process and requirements",
    "Clinical data",
  ];

  const handleRemoveCode = (codeId) => {
    setRemovedCodes((prev) => new Set(prev).add(codeId));
  };

  useEffect(() => {
    try {
      // Loop through the product search results and aggregate by product code
      const codeMap = new Map();
      searchResults.forEach((product) => {
        const productCode = product.productCode;
        const kNumber = product.kNumber ?? product.k_number;
        if (!codeMap.has(productCode)) {
          codeMap.set(productCode, {
            id: codeMap.size + 1,
            productCode: productCode,
            description: product.productCodeDescription,
            productCount: 1,
            highestMatchScore: 1,
            highestIndicationScore: 1,
            device_class: product.deviceClass,
            regulation_number: product.regulationNumber,
            regulation_description: product.regulationDescription,
            medical_specialty: product.medicalSpecialty,
            technicalCharacteristics: product.characteristics || [],
            characteristic_counts: new Map(), // To track characteristic frequencies
          });
          const existingK = codeMap.get(productCode);
          if (!existingK._kNumbers) existingK._kNumbers = new Set();
          if (kNumber) {
            if (!existingK._kNumbers.has(kNumber)) {
              existingK._kNumbers.add(kNumber);
              // console.log('Counting k-number:', kNumber, 'for product code:', productCode);
            }
          }

        } else {
          const existingK = codeMap.get(productCode);
          if (!existingK._kNumbers) existingK._kNumbers = new Set();
          if (kNumber) {
            if (!existingK._kNumbers.has(kNumber)) {
              existingK._kNumbers.add(kNumber);
              existingK.productCount += 1;
              // console.log('Counting k-number:', kNumber, 'for product code:', productCode);
            }
          }
          existingK.highestMatchScore = Math.max(existingK.highestMatchScore, 1);
          existingK.highestIndicationScore = Math.max(existingK.highestIndicationScore, .9);

          //TODO: Why are a lot of these coming up undefined?
          // if (existingK.device_class !== product.deviceClass) {
          //   console.log(`Inconsistent device_class for code ${productCode}: existingK=${existingK.device_class}, new=${product.deviceClass}`);
          // }
          // if (existingK.regulation_number !== product.regulation_number) {
          //   console.log(`Inconsistent regulation_number for code ${productCode}: existingK=${existingK.regulation_number}, new=${product.regulation_number}`);
          // }
          // if (existingK.medical_specialty !== product.medical_specialty) {
          //   console.log(`Inconsistent medical_specialty for code ${productCode}: existingK=${existingK.medical_specialty}, new=${product.medical_specialty}`);
          // }
        }

        // console.log("Counting characteristics for product code:", productCode);

        const existingPC = codeMap.get(productCode);
        for (const { name } of product.characteristics || []) {
          // if (existingPC.characteristic_counts.get(name)) {
          //   console.log(`Characteristic ${name} count for product code ${productCode}: ${existingPC.characteristic_counts.get(name) + 1}`);
          // }
          existingPC.characteristic_counts.set(
            name,
            (existingPC.characteristic_counts.get(name) || 0) + 1
          );
        }
      });

      // console.log("Determining top characteristics for each product code");

      const topCharacteristics = Array.from(codeMap.values()).map(entry => {
        const sortedCharacteristics = Array.from(entry.characteristic_counts.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count }));

        // log to console the top characteristics for this product code
        // console.log(`Top characteristics for product code ${entry.productCode}:`, sortedCharacteristics.slice(0, 8));

        return {
          productCode: entry.productCode,
          count: entry.count,
          characteristics: sortedCharacteristics.slice(0, 8) // pick the top ones
        };
      });

      // console.log("Setting product code map with top characteristics");

      // Loop through top characteristics and set to product code entries
      topCharacteristics.forEach(({ productCode, characteristics }) => {
        //TODO: How are we having undefined product codes here?
        if (productCode === undefined) {
          console.warn("Skipping undefined product code in top characteristics");
          return;
        }

        const entry = codeMap.get(productCode);
        //console.log(`Product code: ${productCode}, characteristics:`, entry.technicalCharacteristics);
        entry.technicalCharacteristics = characteristics.map(c => c.name);
        delete entry.characteristic_counts; // Clean up
      });
      
      setProductCodes(Array.from(codeMap.values()));
    } catch (error) {
      console.error("Error processing search results:", error);
    }
  }, [searchResults]);

  const handleProductCodeSelection = async (selectedCode) => {
    setSelectedProductCode(selectedCode);
    setShowProductCodes(false);
    setShowPredicateBubbleMap(true);
  };

  return (
    <div id="product-code-panel">
        <div>
            <div className="feature-content-heading">Select a Product Code</div>
        </div>
        <div className="product-code-list">
            {productCodes
            .filter((code) => !removedCodes.has(code.id))
            .map((code) => (
                <div key={code.id} className="product-code-card" onClick={() => handleProductCodeSelection(code.productCode)}>
                    <div className="product-code-card-header">
                        <span className="product-code-card-title">{code.productCode}</span>
                        <span className="product-code-card-score">
                            {Math.floor(code.highestIndicationScore * 100)}%
                        </span>
                        <button
                            size="icon"
                            className="product-code-card-remove-button"
                            onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCode(code.id);
                            }}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="space-y-4 cursor-pointer">
                        <p className="text-foreground min-h-[50px]">{code.description}</p>
                        <p className="text-foreground min-h-[30px]">
                        <b>Class</b> {code.device_class ? code.device_class : "N/A"}
                        </p>
                        <p className="text-foreground min-h-[50px]">
                        <b>Reg #</b> {code.regulation_number ? code.regulation_number : "N/A"}
                        <br />
                        {code.regulation_description}
                        </p>
                        <p className="text-foreground min-h-[50px]">
                        <b>Specialty</b> {code.medical_specialty ? code.medical_specialty : "N/A"}
                        </p>
                        {code.technicalCharacteristics.length > 0 ? (
                        <>
                            <b>Technological Characteristics</b>
                            <ul className="list-disc ml-6">
                            {code.technicalCharacteristics.map((characteristic, index) => (
                                <li key={index}>{characteristic}</li>
                            ))}
                            </ul>
                        </>
                        ) : (
                        <p>
                            <b>Technological Characteristics</b>
                            <ul className="list-disc ml-6">
                            {defaultTechnicalCharacteristics.map((characteristic, index) => (
                                <li key={index}>{characteristic}</li>
                            ))}
                            </ul>
                        </p>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t">
                            <span className="text-sm text-muted-foreground">
                                {code.productCount} product{code.productCount !== 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
  );
};

export default ProductCodes;
